import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle, Users, Clock, XCircle } from 'lucide-react';

interface TenantData {
  id: string;
  name: string;
  slug: string;
}

export const LoadingCard: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
    <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Loading Invitation</h2>
    <p className="text-gray-600 dark:text-gray-400">{message}</p>
  </div>
);

export const AcceptedCard: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
      <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
      Welcome to the team!
    </h2>
    <p className="text-gray-600 dark:text-gray-400 mb-4">{message}</p>
    <p className="text-sm text-gray-500 dark:text-gray-400">Redirecting to team settings...</p>
  </div>
);

export const ExpiredCard: React.FC<{ message: string; tenant: TenantData | null }> = ({
  message,
  tenant,
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
    <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
      <Clock className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Invitation Expired</h2>
    <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
    {tenant && (
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Team: <strong>{tenant.name}</strong>
      </p>
    )}
    <Link
      to="/dashboard"
      className="inline-flex items-center gap-2 py-2 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors"
    >
      Go to Dashboard
    </Link>
  </div>
);

export const AlreadyAcceptedCard: React.FC<{
  message: string;
  tenant: TenantData | null;
}> = ({ message, tenant }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
      <CheckCircle className="w-8 h-8 text-blue-600 dark:text-blue-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Already Accepted</h2>
    <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
    {tenant && (
      <Link
        to="/settings/team"
        className="inline-flex items-center gap-2 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
      >
        <Users className="w-5 h-5" />
        Go to Team Settings
      </Link>
    )}
  </div>
);

export const NotFoundCard: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
      <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
      Invitation Not Found
    </h2>
    <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
    <Link
      to="/dashboard"
      className="inline-flex items-center gap-2 py-2 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors"
    >
      Go to Dashboard
    </Link>
  </div>
);

export const ErrorCard: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
      <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
      Something went wrong
    </h2>
    <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-2 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
    >
      Try Again
    </button>
  </div>
);
